import React, { useState, useEffect, useRef } from 'react';

const LabelSuggestionInput = ({
  existingLabels = { labels: [] },
  annotatedList = [],
  onSubmit,
  position = { x: 0, y: 0 },
  onCancel
}) => {
  const [inputValue, setInputValue] = useState('');
  const [filteredSuggestions, setFilteredSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [error, setError] = useState('');
  const inputRef = useRef(null);
  const suggestionsRef = useRef(null);

  const getAvailableLabels = () => {
    const usedLabels = new Set(annotatedList.map(anno => anno.label));
    const labelsArray = existingLabels?.labels || [];
    return labelsArray.filter(label => !usedLabels.has(label.label_name));
  };

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
    const available = getAvailableLabels();
    setFilteredSuggestions(available);
    setShowSuggestions(true);
  }, []);

  const handleInputChange = e => {
    const value = e.target.value;
    if (value.length <= 15) {
      setInputValue(value);
      setError('');

      const available = getAvailableLabels();
      const filtered = available.filter(label =>
        label.label_name.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredSuggestions(filtered);
      setShowSuggestions(true);
      setSelectedIndex(-1);
    } else {
      setError('Label name cannot exceed 15 characters');
    }
  };

  const handleKeyDown = e => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const trimmedInput = inputValue.trim();

      if (selectedIndex >= 0 && filteredSuggestions[selectedIndex]) {
        // User selected from suggestions
        const selected = filteredSuggestions[selectedIndex];
        onSubmit?.({
          label: selected.label_name,
          id: selected.id
        });
      } else if (trimmedInput) {
        // User is trying to add a custom label
        if (isDuplicateLabel(trimmedInput)) {
          setError('This label already exists (case insensitive)');
        } else {
          onSubmit?.({
            label: trimmedInput,
            id: null
          });
        }
      }
    } else if (e.key === 'Escape') {
      e.stopPropagation();
      onCancel?.();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev =>
        prev < filteredSuggestions.length - 1 ? prev + 1 : prev
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => (prev > 0 ? prev - 1 : -1));
    }
  };

  const handleSuggestionClick = suggestion => {
    onSubmit?.({
      label: suggestion.label_name,
      id: suggestion.id
    });
  };

  // Stop propagation of mouse events to prevent canvas interactions
  const handleMouseDown = e => {
    e.stopPropagation();
  };

  const handleClick = e => {
    e.stopPropagation();
  };

  const isDuplicateLabel = newLabel => {
    const normalizedNewLabel = newLabel.toLowerCase().trim();

    const existingLabelsArray = existingLabels?.labels || [];
    const existingLabelNames = existingLabelsArray.map(label =>
      label.label_name.toLowerCase().trim()
    );

    const annotatedLabelNames = annotatedList.map(anno =>
      anno.label.toLowerCase().trim()
    );

    return (
      existingLabelNames.includes(normalizedNewLabel) ||
      annotatedLabelNames.includes(normalizedNewLabel)
    );
  };

  return (
    <div
      style={{
        background: 'white',
        borderRadius: '6px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        width: '270px',
        position: 'relative',
        zIndex: 10000
      }}
      onMouseDown={handleMouseDown}
      onClick={handleClick}
    >
      <div style={{ padding: '8px', display: 'flex', flexDirection: 'column' }}>
        <input
          ref={inputRef}
          type="text"
          style={{
            width: '100%',
            padding: '8px 12px',
            border: '1px solid #e1e1e1',
            borderRadius: '4px',
            fontSize: '14px',
            outline: 'none',
            transition: 'border-color 0.2s, box-shadow 0.2s'
          }}
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          placeholder="Type or select label (ESC to cancel)"
          autoFocus
          onMouseDown={handleMouseDown}
          onClick={handleClick}
        />
        {error && (
          <div
            style={{
              color: 'red',
              fontSize: '10px',
              padding: '4px 12px',
              marginTop: '4px'
            }}
          >
            {error}
          </div>
        )}
      </div>
      {showSuggestions && filteredSuggestions.length > 0 && (
        <div
          ref={suggestionsRef}
          style={{
            maxHeight: '200px',
            overflowY: 'auto',
            padding: '4px 0',
            borderTop: '1px solid #e1e1e1',
            background: 'white',
            borderBottomLeftRadius: '6px',
            borderBottomRightRadius: '6px',
            position: 'relative',
            zIndex: 1000000000000
          }}
          onMouseDown={handleMouseDown}
          onClick={handleClick}
        >
          {filteredSuggestions.map((suggestion, index) => (
            <div
              key={suggestion.id}
              style={{
                position: 'relative',
                padding: '8px 12px',
                cursor: 'pointer',
                fontSize: '14px',
                backgroundColor: index === selectedIndex ? '#f0f7ff' : 'white',
                color: index === selectedIndex ? '#2196f3' : '#333',
                transition: 'all 0.2s ease',
                userSelect: 'none',
                zIndex: 10000,
                ':hover': {
                  backgroundColor: '#f5f5f5'
                }
              }}
              onMouseEnter={() => setSelectedIndex(index)}
              onClick={e => {
                e.stopPropagation();
                handleSuggestionClick(suggestion);
              }}
              onMouseDown={handleMouseDown}
            >
              {suggestion.label_name}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default LabelSuggestionInput;
